<template>
    <div class="font-sans bg-gray-50 text-gray-800">

        <!-- Header -->
        <nav class="fixed w-full z-50 bg-white/90 backdrop-blur-sm shadow-sm">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="flex justify-between h-16">
                    <div class="flex items-center">
                        <img src="../../assets/images/logo-icon-64.png" alt="Logo Resort" class="w-10 h-10 object-contain mr-2" />
                        <a href="#" class="text-primary text-2xl font-bold">Resort <span class="text-secondary">Eco Business</span></a>
                    </div>
                    <div class="hidden md:flex items-center space-x-8">
                        <a v-for="item in links" :key="item.href" :href="item.href" class="nav-link text-secondary hover:text-primary transition">
                            {{ item.label }}
                        </a>
                    </div>
                    <div class="md:hidden flex items-center">
                        <button @click="toggleMenu" class="text-gray-700 hover:text-primary focus:outline-none">
                            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="menuOpen" class="md:hidden bg-white py-2 px-4 shadow-lg">
                <a v-for="item in links" :key="item.href" :href="item.href" class="block py-2 text-secondary hover:text-primary transition">
                    {{ item.label }}
                </a>
            </div>
        </nav>

        <!-- Hero -->
        <header class="relative h-screen overflow-hidden">
            <div class="absolute inset-0 bg-gradient-to-b from-black/20 to-black/60 z-10"></div>
            <div class="parallax bg-[url('../../assets/images/bg/resort-main.jpg')] w-full h-full absolute inset-0 bg-cover bg-center z-0"></div>
            <div class="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-4">
                <h1 class="text-4xl md:text-6xl font-bold text-white mb-6 drop-shadow-lg">Resort Eco Business</h1>
                <p class="text-xl md:text-2xl text-white mb-8 drop-shadow-md">Pertinho da cidade com a tranquilidade do campo</p>
                <a href="#hotel" class="inline-block bg-primary hover:bg-primaryLight text-white font-semibold px-8 py-3 rounded-full transition transform hover:scale-105 shadow-lg">Explore</a>
            </div>
            <div class="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-20">
                <a href="#hotel" class="text-white text-3xl">
                    <i class="fas fa-chevron-down text-white text-3xl animate-bounce-icon"></i>
                </a>
            </div>
        </header>

        <hotel />
        <adega />
        <restaurante />
        <animais />
        <natureza />

        <!-- Footer -->
        <footer class="bg-white/90 backdrop-blur-sm shadow-sm py-12">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="grid md:grid-cols-3 gap-8">
                    <div>
                        <h3 class="text-xl font-bold mb-4">Resort Eco Business</h3>
                        <p class="mb-4">Pertinho da cidade com a tranquilidade do campo</p>
                        <div class="flex space-x-4">
                            <a href="#" class="text-white hover:text-primary transition"><i class="fab fa-facebook-f"></i></a>
                            <a href="#" class="text-white hover:text-primary transition"><i class="fab fa-instagram"></i></a>
                            <a href="#" class="text-white hover:text-primary transition"><i class="fab fa-twitter"></i></a>
                        </div>
                    </div>
                    <div>
                        <h3 class="text-lg font-semibold mb-4">Links Rápidos</h3>
                        <ul class="space-y-2">
                            <li><a href="#hotel" class="hover:text-primary transition">Hotel</a></li>
                            <li><a href="#adega" class="hover:text-primary transition">Adega</a></li>
                            <li><a href="#restaurante" class="hover:text-primary transition">Restaurante</a></li>
                            <li><a href="#animais" class="hover:text-primary transition">Animais</a></li>
                            <li><a href="#natureza" class="hover:text-primary transition">Natureza</a></li>
                        </ul>
                    </div>
                    <div>
                        <h3 class="text-lg font-semibold mb-4">Contato</h3>
                        <ul class="space-y-2">
                            <li class="flex items-center">
                                <i class="fas fa-map-marker-alt mr-2 text-primary"></i>
                                <span>QQ5H+CP - Roxo Roiz em Ponta Grossa - PR</span>
                            </li>
                            <li class="flex items-center">
                                <i class="fas fa-phone-alt mr-2 text-primary"></i>
                                <span>+55 42 98888-7777</span>
                            </li>
                            <li class="flex items-center">
                                <i class="fas fa-envelope mr-2 text-primary"></i>
                                <span>contato@resortecobusiness.com.br</span>
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="border-t border-gray-700 mt-8 pt-8 text-center">
                    <p>&copy; 2025 Resort Eco Business. Todos os direitos reservados.</p>
                    <p class="mt-2">
                        <router-link to="/politica-de-privacidade" class="hover:text-primary transition underline">
                            Política de Privacidade
                        </router-link>
                    </p>
                </div>
            </div>
        </footer>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import hotel from '@/components/index/hotel.vue';
import adega from '@/components/index/adega.vue';
import restaurante from '@/components/index/restaurante.vue';
import animais from '@/components/index/animais.vue';
import natureza from '@/components/index/natureza.vue';

const menuOpen = ref(false);
const toggleMenu = () => (menuOpen.value = !menuOpen.value);

const links = [
    { href: '#hotel', label: 'Hotel' },
    { href: '#adega', label: 'Adega' },
    { href: '#restaurante', label: 'Restaurante' },
    { href: '#animais', label: 'Animais' },
    { href: '#natureza', label: 'Natureza' },
];

onMounted(() => {
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('active')
                entry.target.classList.remove('exiting')
            } else {
                entry.target.classList.remove('active')
                entry.target.classList.add('exiting')
            }
        })
    }, {
        threshold: 0.25
    })

    document.querySelectorAll('.section').forEach(section => {
        observer.observe(section)
    })
})
</script>

<style scoped>
.parallax {
    background-attachment: fixed;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
}

.nav-link {
    position: relative;
}

.nav-link::after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -2px;
    left: 0;
    background-color: #8bb85a;
    transition: width 0.3s ease;
}

.nav-link:hover::after {
    width: 100%;
}

.section {
    opacity: 0;
    transition: opacity 0.5s ease;
}

.section.active {
    opacity: 1;
    animation: fadeIn 0.8s ease-in-out;
}

.section.exiting {
    opacity: 0;
    animation: fadeOut 0.5s ease-out;
}

@keyframes fadeIn {
    0% {
        opacity: 0;
    }

    100% {
        opacity: 1;
    }
}

@keyframes fadeOut {
    0% {
        opacity: 1;
    }

    100% {
        opacity: 0;
    }
}

@media (max-width: 768px) {
    .parallax {
        background-attachment: scroll;
    }
}

@keyframes bounceY {

    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-10px);
    }
}

.animate-bounce-icon {
    animation: bounceY 1.5s infinite;
}
</style>